import cn from 'clsx'
import React, { Dispatch, FC, SetStateAction } from 'react'
import s from './VariantRound.module.css'
import type {
  Product,
  ProductOption,
  ProductOptionValues,
  ProductVariant,
} from '@commerce/types/product'
import { SelectedOptions } from '@components/product/helpers'
import { useRouter } from 'next/router'
import { getValidOptionsForMetal } from '../VariantRectangle/helpers'

export interface VariantRoundProps {
  optionType: string
  optionValue: string | undefined
  className?: string
  opt: ProductOption
  setSelectedOptions: React.Dispatch<React.SetStateAction<SelectedOptions>>
  product: Product
  setVariantSettings: Dispatch<
    SetStateAction<{
      metal: string
      diam_type: string
      tcw: string
      size: string
    }>
  >
  variantSettings: {
    metal: string
    diam_type: string
    tcw: string
    size: string
  }
  currentFilters?: string[]
}

const VariantRound: FC<VariantRoundProps> = (props) => {
  const {
    optionType,
    optionValue,
    className,
    opt,
    setSelectedOptions,
    product,
    variantSettings,
    setVariantSettings,
  } = props
  const rootClassName = cn(s.root, className)

  const router = useRouter()

  const handleOnClickButton = (
    product: Product,
    opt: ProductOption,
    v: ProductOptionValues
  ) => {
    router.push(
      `/product/${product.slug}-${v.label.toLowerCase().replaceAll(' ', '-')}`,
      undefined,
      { shallow: true }
    );
  
    const newMetal = v.label;
  
    // Get valid options for diamond type, tcw and size
    const validDiamTypes = getValidOptionsForMetal(newMetal, variantSettings.diam_type, product.variants, 'diamond type');
    const validTcwOptions = getValidOptionsForMetal(newMetal, variantSettings.diam_type, product.variants, 'total carat weight');
    const validSizeOptions = getValidOptionsForMetal(newMetal, variantSettings.diam_type, product.variants, 'size');
  
    // Check if the current options are valid
    const isValidCurrentDiamType = validDiamTypes.includes(variantSettings.diam_type);
    const isValidCurrentTCW = validTcwOptions.includes(variantSettings.tcw);
    const isValidCurrentSize = validSizeOptions.includes(variantSettings.size);
  
    setVariantSettings({
      metal: newMetal,
      diam_type: isValidCurrentDiamType ? variantSettings.diam_type : validDiamTypes[0] || '',
      tcw: isValidCurrentTCW ? variantSettings.tcw : validTcwOptions[0] || '',
      size: isValidCurrentSize ? variantSettings.size : validSizeOptions[0] || '',
    });
  };

  return (
    <div className={`${rootClassName} ${s.root}`}>
      <div className={s.header}>
        <span className={s.type}>{optionType.toLowerCase()}:</span>
        <span className={s.title}>{optionValue}</span>
      </div>
      <div className={s.container}>
        {opt.values.map((vl, i) => {
          const thisVal =
            {
              'White Gold 14K': ['14', 'border-whiteGold'],
              'Yellow Gold 14K': ['14', 'border-yellowGold'],
              'Rose Gold 14K': ['14', 'border-roseGold'],
              'White & Yellow Gold 14K': ['14', s.whiteAndYellow],
              'White Gold 18K': ['18', 'border-whiteGold'],
              'Yellow Gold 18K': ['18', 'border-yellowGold'],
              'Rose Gold 18K': ['18', 'border-roseGold'],
              'White & Yellow Gold 18K': ['18', s.whiteAndYellow],
              Platinum: ['PT', 'platinum'],
            }[vl.label] || []
          return (
            <div
              key={`${thisVal[0]}-${i}`}
              className={cn(s.bigCircle, {
                '!border-accent-9': optionValue === vl.label,
              })}
              onClick={() => handleOnClickButton(product, opt, vl)}
            >
              <div className={`${s.tinyCircle} ${thisVal[1]}`}>
                <span className="pb-[1px]">
                  {thisVal[0]}
                  {optionValue === vl.label && vl.label !== 'Platinum'
                    ? 'K'
                    : ''}
                </span>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default React.memo(VariantRound)
